import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { addToCartAndOpenDrawer } from '@theme/cart-quick-add';

/**
 * Lazily-created, shared `quick-add-component` instance used to drive the global
 * quick-add modal from the collection tabs slider. Replicates the singleton
 * pattern from `assets/quick-add-slider.js` so both components share the same
 * appended element.
 *
 * @type {QuickAddComponent | null}
 */
let sharedQuickAdd = null;

/**
 * @returns {Promise<QuickAddComponent>}
 */
async function getQuickAdd() {
  await customElements.whenDefined('quick-add-component');

  if (!sharedQuickAdd) {
    sharedQuickAdd = /** @type {QuickAddComponent} */ (document.createElement('quick-add-component'));
    sharedQuickAdd.hidden = true;
    document.body.appendChild(sharedQuickAdd);
  }

  return sharedQuickAdd;
}

/**
 * A tabbed collection slider section (e.g. Men's / Women's Best Sellers).
 *
 * All tab panels are pre-rendered in the DOM; only the active panel is shown.
 * Inactive panels carry the `hidden` attribute so their slideshows are not
 * composited until needed. Switching tabs:
 *   1. Toggles `aria-selected` on the tab buttons.
 *   2. Shows/hides panels via the `hidden` attribute.
 *   3. Re-selects slide 0 on the newly-visible `slideshow-component` via its
 *      public `select()` method so the scroll position and arrow/dot state are
 *      correct after the panel was display-none'd.
 *
 * Each card carries `.ct-card__size` pills that toggle `aria-pressed`. Behavior
 * depends on the section's `size_add_to_cart_mode` setting, mirrored on
 * the host element as the presence/absence of `data-direct-add`:
 *   - Dialog mode (`data-direct-add` absent): size pills only preselect the
 *     variant; clicking the "+" button opens the global quick-add preview
 *     modal pre-selected to the chosen variant (or the card's default variant
 *     if none is pressed).
 *   - Direct-add mode (`data-direct-add` present, "+" button hidden via
 *     Liquid): clicking a size pill resolves the exact variant for the
 *     selected color + size and adds it straight to the cart, opening the
 *     cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs   - The `ref="tabs[]"` tab buttons in the `[role="tablist"]`.
 * @property {HTMLElement[]}       panels - The `ref="panels[]"` `.ct__panel` wrappers.
 *
 * @extends {Component<Refs>}
 */
class CollectionTabsSlider extends Component {
  /**
   * Handles a tab button click: activates the chosen tab panel and deactivates
   * the rest. After revealing the panel it resets the contained slideshow to
   * slide 0 so arrows and scroll state are correct.
   *
   * The `on:click="/selectTab"` attribute on each `ref="tabs[]"` button proxies
   * `event.target` to the button element itself (Component convention).
   *
   * @param {Event} event - The proxied click event; `event.target` is the button.
   */
  selectTab = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const index = button.dataset.tabIndex;

    if (index === undefined) return;

    // Update aria-selected on all tab buttons.
    const tabs = /** @type {HTMLButtonElement[] | undefined} */ (this.refs.tabs);
    if (Array.isArray(tabs)) {
      for (const tab of tabs) {
        tab.setAttribute('aria-selected', tab.dataset.tabIndex === index ? 'true' : 'false');
      }
    }

    // Show the matching panel; hide all others.
    const panels = /** @type {HTMLElement[] | undefined} */ (this.refs.panels);
    if (Array.isArray(panels)) {
      for (const panel of panels) {
        if (panel.dataset.tabIndex === index) {
          panel.removeAttribute('hidden');
          // Re-initialise the slideshow inside the newly-visible panel.
          // `Slideshow.select()` is the only public recalc entry-point: it
          // resets scroll position, updates aria-hidden on slides, and
          // re-evaluates prev/next button state — all of which are stale when
          // the panel was `display:none` (hidden attribute).
          this.#recalcSlideshow(panel);
        } else {
          panel.setAttribute('hidden', '');
        }
      }
    }

    // Keep the host element's data-active attribute in sync for CSS targeting.
    this.dataset.active = index;
  };

  /**
   * Handles a color-swatch button click: marks the clicked swatch as pressed,
   * clears the others within the same card, optionally swaps the card image to
   * the swatch's variant image, and records the active variant on the card root
   * so that quick-add always opens on the selected color.
   *
   * @param {Event} event - The proxied click event; `event.target` is the `.ct-card__swatch` button.
   */
  selectSwatch = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const card = button.closest('[data-product-row]');
    if (!card) return;

    for (const sw of card.querySelectorAll('.ct-card__swatch')) {
      sw.setAttribute('aria-pressed', 'false');
    }
    button.setAttribute('aria-pressed', 'true');

    // Swap the card image to the color's variant image, if provided.
    const mediaSrc = /** @type {string | undefined} */ (button.dataset.mediaSrc);
    if (mediaSrc) {
      const img = /** @type {HTMLImageElement | null} */ (card.querySelector('img.ct-card__image'));
      if (img) {
        // Swap srcset first: with a srcset present the browser picks from it and
        // ignores src, so replacing both keeps the swapped image responsive
        // instead of forcing one fixed-width file.
        img.srcset = /** @type {string} */ (button.dataset.mediaSrcset ?? '');
        img.src = mediaSrc;
      }
    }

    // Size pills are rendered against the default color only; re-derive their
    // availability and variant ids for the newly selected color.
    const color = button.dataset.color;
    const map = this.#variantMap(/** @type {HTMLElement} */ (card));
    /** @type {HTMLElement | null} */
    let pressedSize = null;

    if (color && map) {
      for (const pill of /** @type {NodeListOf<HTMLButtonElement>} */ (card.querySelectorAll('.ct-card__size'))) {
        const variant = map.find((v) => v.options.includes(color) && v.options.includes(pill.dataset.size ?? ''));

        pill.disabled = !variant?.available;
        if (variant) {
          pill.dataset.variantId = String(variant.id);
        } else {
          delete pill.dataset.variantId;
        }

        if (pill.getAttribute('aria-pressed') === 'true') {
          if (pill.disabled) {
            pill.setAttribute('aria-pressed', 'false');
          } else {
            pressedSize = pill;
          }
        }
      }
    }

    // Track the active variant so quick-add opens on this color (and the
    // still-selected size, if it exists in this color).
    const variantId = pressedSize?.dataset.variantId || button.dataset.variantId;
    if (variantId) {
      /** @type {HTMLElement} */ (card).dataset.variantId = variantId;
      this.#syncCardLinks(/** @type {HTMLElement} */ (card), variantId);
    }
  };

  /**
   * Handles a size-pill click: marks the clicked pill as pressed and clears
   * the others within the same card.
   *
   * - Two-step mode (`size_add_to_cart_mode` is 'two-step', so the host
   *   element has no `data-direct-add` attribute): preserves the exact prior
   *   behavior — just records the active variant on the card root so that
   *   quick-add opens on the selected size.
   * - Direct-add mode (`data-direct-add` present on the host, i.e.
   *   `size_add_to_cart_mode` is 'one-click', so the "+" button is hidden): resolves
   *   the exact variant for the currently selected color + the clicked size
   *   using the card's `[data-ct-variant-map]` JSON, then adds it straight to
   *   the cart and opens the cart drawer instead of merely preselecting it.
   *
   * @param {Event} event - The proxied click event; `event.target` is the pill button.
   * @returns {Promise<void>}
   */
  selectSize = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const card = /** @type {HTMLElement | null} */ (button.closest('[data-product-row]'));
    if (!card) return;

    for (const pill of card.querySelectorAll('.ct-card__size')) {
      pill.setAttribute('aria-pressed', 'false');
    }
    button.setAttribute('aria-pressed', 'true');

    if (!this.hasAttribute('data-direct-add')) {
      // Track the active variant so quick-add opens on the selected size.
      if (button.dataset.variantId) {
        card.dataset.variantId = button.dataset.variantId;
        this.#syncCardLinks(card, button.dataset.variantId);
      }
      return;
    }

    // Direct-add mode: resolve the exact variant for the selected color +
    // clicked size, then add it to the cart and open the drawer.
    const variantId = this.#resolveVariantForSize(card, button.dataset.size, button.dataset.variantId);

    if (!variantId) return;

    await addToCartAndOpenDrawer(variantId, { sourceId: this.id || 'collection-tabs-slider' });
  };

  /**
   * Handles the quick-add "+" button click (two-step mode only; the button is
   * hidden in one-click mode).
   *
   * The "+" is adaptive so it works regardless of the global
   * `enable_quick_add_dialog` theme setting:
   *   - Preview available (`#quick-add-dialog` present in the DOM, i.e. the
   *     dialog setting is on): opens the rich quick-add preview modal
   *     pre-selected to the tracked variant — the exact prior behavior.
   *   - Preview unavailable (dialog setting off, so the modal markup is not
   *     rendered): adds the selected color + size variant — or the card's
   *     default variant when no size is pressed — straight to the cart and
   *     opens the drawer. This mirrors the reference collection card's
   *     two-step "Add" button so the "+" still adds the product.
   *
   * @param {Event} event - The proxied click event; `event.target` is the "+" button.
   * @returns {Promise<void>}
   */
  quickAdd = async (event) => {
    const card = /** @type {HTMLElement | null} */ (
      /** @type {Element} */ (event.target).closest('[data-product-row]')
    );

    if (!card || !card.dataset.productUrl) return;

    const pressedSize = /** @type {HTMLElement | null} */ (
      card.querySelector('.ct-card__size[aria-pressed="true"]')
    );

    // No preview modal in the DOM: add straight to the cart instead of opening
    // a dialog that would never appear.
    if (!document.getElementById('quick-add-dialog')) {
      const variantId = pressedSize?.dataset.size
        ? this.#resolveVariantForSize(card, pressedSize.dataset.size, pressedSize.dataset.variantId)
        : card.dataset.variantId;

      if (!variantId) return;

      await addToCartAndOpenDrawer(variantId, { sourceId: this.id || 'collection-tabs-slider' });
      return;
    }

    // Preview modal available: prefer the variant tracked live by
    // selectSwatch/selectSize on the card root; fall back to a pressed size
    // pill, then to whatever the card shipped with.
    const variantId = card.dataset.variantId || pressedSize?.dataset.variantId || '';

    const url = new URL(card.dataset.productUrl, window.location.origin);
    if (variantId) url.searchParams.set('variant', variantId);

    const quickAdd = await getQuickAdd();
    await quickAdd.openForUrl(url.toString());
  };

  /**
   * Advances the visible panel's slideshow to the previous slide.
   * Bound to the header `on:click="/previous"` arrow in carousel layout.
   */
  previous = () => {
    this.#activeSlideshow()?.previous();
  };

  /**
   * Advances the visible panel's slideshow to the next slide.
   * Bound to the header `on:click="/next"` arrow in carousel layout.
   */
  next = () => {
    this.#activeSlideshow()?.next();
  };

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Resolves the exact variant id for the currently selected color + the given
   * size using the card's `[data-ct-variant-map]` JSON. Shared by the one-click
   * size-pill path and the two-step "+" add-to-cart fallback so both resolve
   * variants identically.
   *
   * @param {HTMLElement} card - The `.ct-card` root (`[data-product-row]`).
   * @param {string | undefined} size - The clicked/pressed size value.
   * @param {string | null | undefined} fallbackVariantId - Variant id to use when
   *   the map is missing or malformed.
   * @returns {string | number | null | undefined} The resolved variant id, `null`
   *   when the color + size combo exists but is unavailable, or the fallback
   *   otherwise.
   */
  #resolveVariantForSize(card, size, fallbackVariantId) {
    const pressedSwatch = /** @type {HTMLElement | null} */ (
      card.querySelector('.ct-card__swatch[aria-pressed="true"]')
    );
    const color = pressedSwatch?.dataset.color || null;

    /** @type {string | number | null | undefined} */
    let variantId = fallbackVariantId;

    const map = this.#variantMap(card);
    if (map && size) {
      const variant = map.find((v) => (!color || v.options.includes(color)) && v.options.includes(size));
      variantId = variant && variant.available ? variant.id : variant ? null : fallbackVariantId;
    }

    return variantId;
  }

  /**
   * Parses the card's `[data-ct-variant-map]` JSON.
   *
   * @param {HTMLElement} card - The `.ct-card` root (`[data-product-row]`).
   * @returns {{ id: number, available: boolean, options: string[] }[] | null} The
   *   variants, or null when the map is missing or malformed.
   */
  #variantMap(card) {
    const mapScript = card.querySelector('[data-ct-variant-map]');
    if (!mapScript) return null;

    try {
      return JSON.parse(mapScript.textContent ?? '[]');
    } catch {
      return null;
    }
  }

  /**
   * Points the card's product links at the given variant so the product page
   * opens on the color/size picked on the card.
   *
   * @param {HTMLElement} card - The `.ct-card` root (`[data-product-row]`).
   * @param {string} variantId - The variant to link to.
   */
  #syncCardLinks(card, variantId) {
    if (!card.dataset.productUrl) return;

    const url = new URL(card.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    for (const link of card.querySelectorAll('a.ct-card__media-link, a.ct-card__title')) {
      link.setAttribute('href', url.pathname + url.search);
    }
  }

  /**
   * Returns the `slideshow-component` inside the currently-visible panel
   * (the one without the `hidden` attribute), or null.
   * @returns {(HTMLElement & { next?: Function, previous?: Function }) | null}
   */
  #activeSlideshow() {
    const panels = this.refs.panels;
    let panel = null;
    if (Array.isArray(panels)) {
      panel = panels.find((p) => !p.hasAttribute('hidden')) ?? null;
    } else if (panels instanceof HTMLElement) {
      panel = panels.hasAttribute('hidden') ? null : panels;
    }
    panel ??= this.querySelector('.ct__panel:not([hidden])');
    return panel ? panel.querySelector('slideshow-component') : null;
  }

  /**
   * Resets the `slideshow-component` inside a newly-visible panel so its scroll
   * position and control state are valid.
   *
   * **Why `select(0)`?**
   * `Slideshow` initialises in `connectedCallback` via a `requestAnimationFrame`
   * callback, but when the panel had `hidden` (display:none), layout dimensions
   * were zero. The `#resizeObserver` inside `Slideshow` watches
   * `refs.slideshowContainer` and fires on subsequent layout changes, but only
   * updates visible-slide counts and control visibility — it does NOT reset the
   * scroll offset to a valid position after the container goes from 0-width to
   * real-width.
   *
   * `Slideshow.select()` is the only **public** method that:
   *   - Calls `this.#scroll.to(slide, { instant })` to snap to a real position.
   *   - Updates `this.current`, prev/next button disabled state, and aria-hidden
   *     on slides.
   *
   * We therefore call `select(0, undefined, { animate: false })` after a
   * `requestAnimationFrame` so the browser has committed the un-hidden layout
   * before we read/write scroll geometry.
   *
   * @param {HTMLElement} panel - The panel that was just made visible.
   */
  #recalcSlideshow(panel) {
    /** @type {import('./slideshow').Slideshow | null} */
    const slideshow = panel.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    // One rAF is enough: the `hidden` attribute removal already triggers a
    // synchronous style recalc, so by the time the frame callback runs the
    // layout dimensions are valid.
    requestAnimationFrame(() => {
      slideshow.select(0, undefined, { animate: false });
    });
  }
}

if (!customElements.get('collection-tabs-slider')) {
  customElements.define('collection-tabs-slider', CollectionTabsSlider);
}
